import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Building2, Landmark, CalendarClock, Upload } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { hasAdminSession } from "@/lib/session";

const FIELD = (label, key, form, setForm, ph = "") => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1" placeholder={ph} data-testid={`business-${key}`} />
  </div>
);

export default function Settings() {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [goLive, setGoLive] = useState("");
  const [savingGoLive, setSavingGoLive] = useState(false);
  const [openingFile, setOpeningFile] = useState(null);
  const [importingOpening, setImportingOpening] = useState(false);
  const [openingResult, setOpeningResult] = useState(null);

  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/admin/business").then(({ data }) => setForm(data));
    api.get("/opening/go-live").then(({ data }) => setGoLive(data?.go_live_date || "")).catch(() => {});
    // eslint-disable-next-line
  }, []);

  if (!form) return <AdminShell title="Business Settings"><div className="py-24 text-center text-muted-foreground">Loading...</div></AdminShell>;

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/business", form); toast.success("Business details saved — invoices updated"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const saveGoLive = async () => {
    if (!goLive) { toast.error("Pick a Go-Live date"); return; }
    setSavingGoLive(true);
    try {
      await api.put("/opening/go-live", { go_live_date: goLive });
      toast.success(`Go-Live date set: ${goLive}. Historical entries before this date are treated as opening data.`);
    } catch { toast.error("Save failed"); } finally { setSavingGoLive(false); }
  };

  const importOpeningStock = async () => {
    if (!openingFile) { toast.error("Choose a CSV or Excel file"); return; }
    setImportingOpening(true); setOpeningResult(null);
    const fd = new FormData(); fd.append("file", openingFile);
    try {
      const { data } = await api.post("/inventory/master-bills/import-opening", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOpeningResult(data);
      toast.success(`Opening pieces: ${data.created} piece(s) itemized under ${data.bill?.bill_no}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImportingOpening(false); }
  };

  return (
    <AdminShell title="Business Settings">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Building2 className="h-5 w-5 text-[hsl(var(--primary))]" /><h3 className="font-medium">Company & Tax Details</h3></div>
          <div className="space-y-3">
            {FIELD("Company Name", "company_name", form, setForm)}
            {FIELD("GSTIN", "gstin", form, setForm, "27ABCDE1234F1Z5")}
            {FIELD("Address Line 1", "address_line1", form, setForm)}
            {FIELD("Address Line 2", "address_line2", form, setForm)}
            <div className="grid grid-cols-3 gap-3">
              {FIELD("City", "city", form, setForm)}
              {FIELD("State", "state", form, setForm)}
              {FIELD("Pincode", "pincode", form, setForm)}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {FIELD("Phone", "phone", form, setForm)}
              {FIELD("Email", "email", form, setForm)}
            </div>
            {FIELD("Logo URL (optional)", "logo_url", form, setForm)}
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4"><Landmark className="h-5 w-5 text-[#8E6F33]" /><h3 className="font-medium">Bank & Payment Details</h3></div>
          <div className="space-y-3">
            {FIELD("Bank Name", "bank_name", form, setForm)}
            {FIELD("Account Holder Name", "account_name", form, setForm)}
            {FIELD("Account Number", "account_number", form, setForm)}
            <div className="grid grid-cols-2 gap-3">
              {FIELD("IFSC Code", "ifsc", form, setForm)}
              {FIELD("Branch", "branch", form, setForm)}
            </div>
            {FIELD("UPI ID", "upi_id", form, setForm)}
            <div>
              <Label className="text-xs">Invoice Footer Notes</Label>
              <Textarea value={form.invoice_notes || ""} onChange={(e) => setForm({ ...form, invoice_notes: e.target.value })} rows={3} className="mt-1" placeholder="e.g. Goods once sold will not be taken back. Subject to jurisdiction." />
            </div>
          </div>
        </Card>

        <div className="lg:col-span-2">
          <Button data-testid="business-save-button" onClick={save} disabled={saving} className="gap-2 h-12 w-full sm:w-auto"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Business Details"}</Button>
        </div>

        <div className="lg:col-span-2">
          <Card className="p-6" data-testid="go-live-card">
            <div className="flex items-center gap-2 mb-1"><CalendarClock className="h-5 w-5 text-[hsl(var(--primary))]" /><h3 className="font-medium">Go-Live Date & Opening Balances</h3></div>
            <p className="text-xs text-muted-foreground mb-4">
              Set the date your books moved onto Maahey. Enter opening balances on each Supplier / Karigar / Customer record. For opening stock, upload a CSV with <span className="font-medium">one row per physical piece</span> — columns <span className="font-mono">sku,gross_weight_g,net_weight_g,diamond_carat,purity,unit_cost,notes</span>. Each piece is itemized under an automatic <span className="font-mono">Opening Balance / Self</span> Master Bill.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label className="text-xs">Go-Live Date</Label>
                <Input data-testid="go-live-date-input" type="date" value={goLive} onChange={(e) => setGoLive(e.target.value)} className="mt-1 font-mono" />
              </div>
              <div>
                <Button data-testid="go-live-save-button" onClick={saveGoLive} disabled={savingGoLive} className="gap-2 h-10"><Save className="h-4 w-4" /> {savingGoLive ? "Saving..." : "Save Go-Live Date"}</Button>
              </div>
              <div />
              <div>
                <Label className="text-xs">Opening Stock CSV / Excel</Label>
                <label className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border bg-[#F7F0E6] p-3 cursor-pointer mt-1">
                  <span className="text-xs">{openingFile ? openingFile.name : "Choose file (one row per piece)"}</span>
                  <input data-testid="opening-stock-file-input" type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={(e) => setOpeningFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              <div>
                <Button data-testid="import-opening-stock-button" onClick={importOpeningStock} disabled={importingOpening || !openingFile} className="gap-2 h-10"><Upload className="h-4 w-4" /> {importingOpening ? "Importing..." : "Import Opening Pieces"}</Button>
              </div>
              <div />
            </div>
            {openingResult && (
              <div data-testid="opening-import-result" className="mt-3 rounded-lg border border-border p-3 text-sm">
                <div className="text-[#1F5B2E] font-medium">Created {openingResult.created} piece(s) · Bill <span className="font-mono">{openingResult.bill?.bill_no}</span></div>
                {openingResult.errors?.length > 0 && (
                  <ul className="mt-2 text-xs text-destructive list-disc ml-4 max-h-32 overflow-y-auto">
                    {openingResult.errors.map((er, i) => <li key={`${er}-${i}`}>{er}</li>)}
                  </ul>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-4">
              Opening balances for individual Suppliers / Karigars / Customers are set on their record (open the customer, supplier, or karigar detail and enter opening gold (g) and INR). Ledgers automatically pick these up.
            </p>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}
